import React from 'react';
import { Plane, Settings, BarChart3, DollarSign, CheckCircle2 } from 'lucide-react';
import { services } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const iconMap = {
  Plane: Plane,
  Settings: Settings,
  BarChart3: BarChart3,
  DollarSign: DollarSign
};

const Services = () => {
  const serviceDetails = [
    {
      title: "Operating Leases",
      icon: "Plane",
      description: "Flexible operating lease solutions for commercial aircraft",
      features: [
        "Narrow-body and wide-body aircraft options",
        "Airbus A320 family including A320neo variants",
        "Boeing 737 family including 737 MAX",
        "Modern, fuel-efficient fleet (average age 6.8 years)",
        "Flexible lease terms from 3 to 12 years",
        "Competitive lease rates structured for African markets",
        "Maintenance reserve programs available",
        "End-of-lease transition support"
      ]
    },
    {
      title: "Engine Leasing & Management",
      icon: "Settings",
      description: "Comprehensive engine leasing and asset management programs",
      features: [
        "LEAP-1A and LEAP-1B engine families",
        "CFM56-5B and CFM56-7B powerplants",
        "Pratt & Whitney PW1100G GTF engines",
        "Spare engine short-term lease programs",
        "Engine pool access during maintenance events",
        "Technical records management",
        "Shop visit financing options",
        "Strategic remarketing services"
      ]
    },
    {
      title: "Portfolio Management",
      icon: "BarChart3",
      description: "End-to-end aviation asset lifecycle management",
      features: [
        "Aircraft acquisition and sourcing",
        "Technical due diligence and inspections",
        "Lease management and administration",
        "Maintenance oversight and compliance",
        "Records audit and documentation",
        "Asset remarketing and sales",
        "Part-out and disassembly management",
        "Portfolio optimization consulting"
      ]
    },
    {
      title: "Financing Solutions",
      icon: "DollarSign",
      description: "Customized aircraft financing structures for airlines",
      features: [
        "Sale-leaseback transactions",
        "Secured aircraft lending",
        "Pre-delivery payment financing",
        "Bridge financing for aircraft deliveries",
        "Engine financing and leasing structures",
        "Debt and equity instruments",
        "Structured finance solutions",
        "Capital markets access and advisory"
      ]
    }
  ];

  return (
    <div className="services-page">
      <SEO 
        title="Aircraft Leasing & Financing Services"
        description="Comprehensive aircraft leasing services including operating leases, engine leasing, portfolio management, and financing solutions for airlines across Africa."
        keywords="aircraft operating lease, engine leasing, aircraft financing, aviation portfolio management, sale-leaseback"
        canonicalUrl="/services"
      />
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">Our Services</h1>
          <p className="page-subtitle">
            Comprehensive aircraft leasing, financing, and asset management solutions
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview-section">
        <div className="services-overview-grid">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card key={service.id} className="service-overview-card">
                <CardContent className="service-overview-content">
                  <div className="service-overview-icon">
                    <IconComponent size={36} />
                  </div>
                  <h3 className="service-overview-title">{service.title}</h3>
                  <p className="service-overview-description">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Detailed Services */}
      <section className="services-detailed-section">
        <div className="services-detailed-header">
          <h2 className="section-title">Service Capabilities</h2>
          <p className="section-subtitle">
            Explore our comprehensive aircraft leasing and financing solutions
          </p>
        </div>

        <div className="services-detailed-container">
          {serviceDetails.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div key={index} className="service-detailed-item">
                <div className="service-detailed-header-row">
                  <div className="service-detailed-icon-wrapper">
                    <IconComponent size={32} />
                  </div>
                  <div className="service-detailed-header-text">
                    <h3 className="service-detailed-title">{service.title}</h3>
                    <p className="service-detailed-description">{service.description}</p>
                  </div>
                </div>
                <div className="service-detailed-features">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="service-feature-item">
                      <CheckCircle2 size={18} className="feature-check-icon" />
                      <span className="feature-text">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="services-cta-content">
          <h2 className="services-cta-title">Let's Discuss Your Needs</h2>
          <p className="services-cta-text">
            Our team is ready to structure a leasing or financing solution tailored to your operational requirements
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;